// client for GET /api/dashboard-stats — the requirements-based completeness
// aggregation behind the dashboard statistics section. every % is derived
// from the same per-system requirement predicates as the project checklist
// (RA data + asset inventory, substitutable sources), so dashboard and
// project views always agree.

import { request } from '@/lib/apiClient'

export interface StatsHero {
  overall_pct: number       // % of active systems with BOTH requirements met
  // the library's size and the work, from ONE grouping so the dashboard
  // and /system-library can never disagree
  distinct_systems: number
  installations: number
  vessels: number
  // documents that named a real product the register has no system for
  documents_identified_no_system: number
  distinct_products_with_no_system: number
  systems_total: number
  systems_complete: number
  ra_done: number
  inv_done: number
}

export interface StatsByType {
  key: 'ra' | 'inventory' | 'word' | 'excel'
  label: string
  n: number
  total: number
  pct: number
}

export interface StatsTrendPoint {
  date: string       // yyyy-mm-dd
  systems: number    // cumulative assessments
  details: number    // cumulative per-system RA details
}

export interface StatsSnapshot {
  date: string
  overall_pct: number  // requirements-met % (older rows: retired density metric)
  word_pct: number
  excel_pct: number
  asset_pct: number
}

export interface StatsLeaderboardRow {
  id: string
  hull_number: string
  vessel_name: string | null
  systems: number
  complete: number
  ra_done: number
  inv_done: number
  pct: number
}

export interface StatsWeek {
  systems_7d: number
  details_7d: number
  systems_30d: number
  details_30d: number
}

export interface DashboardStats {
  hero: StatsHero
  by_type: StatsByType[]
  ra_partial: number   // systems with only a thin Word RA (no other source)
  trend: StatsTrendPoint[]
  snapshots: StatsSnapshot[]
  leaderboard: StatsLeaderboardRow[]
  week: StatsWeek
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data, error } = await request<DashboardStats>('GET', '/api/dashboard-stats')
  if (error || !data) throw new Error(error?.message ?? 'Failed to load dashboard stats')
  return data
}
